import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CompareAnalysisModal from './CompareAnalysisModal';
import DualRangeSlider from './DualRangeSlider';
import { useNumberInput } from '../hooks/useNumberInput';
import './LandingPage.css';
import ComingSoonModal from './ComingSoonModal';

interface LandingPageProps {
  darkMode: boolean;
  onSearch: (filters: {
    position: string;
    salary: [number, number];
    location: string;
    remote: boolean;
    experience: string;
  }) => void;
  onLoginRequired?: (action: string) => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ darkMode, onSearch, onLoginRequired }) => {
  const { isAuthenticated } = useAuth();
  const [position, setPosition] = useState('');
  const [salaryMin, setSalaryMin] = useState<string>('50000');
  const [salaryMax, setSalaryMax] = useState<string>('150000');
  const [salaryRange, setSalaryRange] = useState<[number, number]>([50000, 150000]);
  const [location, setLocation] = useState('');
  const [remote, setRemote] = useState(false);
  const [experience, setExperience] = useState('');
  const [showComparison, setShowComparison] = useState(false);
  const [currentPosition, setCurrentPosition] = useState('');
  const [currentLocation, setCurrentLocation] = useState('');
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  
  const currentSalaryInput = useNumberInput({
    initialValue: '',
    min: 0,
    max: 10000000,
    step: 1000,
    allowEmpty: false
  });

  // Listen for the custom event to trigger comparison search
  React.useEffect(() => {
    const handleTriggerSearch = () => {
      handleComparisonSearch();
    };

    window.addEventListener('triggerComparisonSearch', handleTriggerSearch);
    return () => {
      window.removeEventListener('triggerComparisonSearch', handleTriggerSearch);
    };
  }, [currentPosition, currentSalaryInput.value, currentLocation]);

  const popularPositions = [
    'Software Engineer',
    'Data Scientist',
    'Product Manager',
    'UX Designer',
    'DevOps Engineer',
    'Business Analyst',
    'Marketing Manager',
    'Sales Manager'
  ];

  const experienceLevels = [
    'Entry Level (0-2 years)',
    'Junior (2-4 years)',
    'Mid-Level (4-7 years)',
    'Senior (7-10 years)',
    'Lead/Principal (10+ years)'
  ];

  const handleSearch = () => {
    onSearch({
      position,
      salary: salaryRange,
      location,
      remote,
      experience
    });
  };

  const handleComparisonSearch = () => {
    const salaryValue = parseFloat(currentSalaryInput.value);
    if (currentPosition && currentSalaryInput.value && !isNaN(salaryValue) && currentLocation) {
      // Close the modal if it's open
      setShowCompareModal(false);
      
      // Use the search function with comparison data
      onSearch({
        position: currentPosition,
        salary: [salaryValue * 0.8, salaryValue * 1.2],
        location: currentLocation,
        remote: false,
        experience: ''
      });
    } else {
      alert('Please fill in all comparison fields first');
    }
  };

  const formatSalary = (value: number) => {
    return `$${value.toLocaleString()}`;
  };

  return (
    <div className={`landing-page ${darkMode ? 'dark' : ''}`}>
      <div className="landing-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            Find Your Perfect Job Match
            <span className="hero-subtitle">Where Data Meets Opportunity</span>
          </h1>
          <p className="hero-description">
            Discover salary insights, compare opportunities across countries, and make data-driven career decisions
          </p>
        </div>
      </div>

      <div className="landing-main">
        <div className="search-section">
          <h2>What are you looking for?</h2>
          
          <div className="search-form">
            <div className="form-group">
              <label htmlFor="position">Position/Job Title</label>
              <input
                type="text"
                id="position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                placeholder="e.g., Software Engineer, Data Scientist"
                className="form-input"
                list="position-suggestions"
              />
              <datalist id="position-suggestions">
                {popularPositions.map(pos => (
                  <option key={pos} value={pos} />
                ))}
              </datalist>
            </div>

            <div className="form-group">
              <label>Desired Salary Range</label>
              <DualRangeSlider
                min={0}
                max={300000}
                step={1000}
                value={salaryRange}
                onChange={(value) => {
                  setSalaryRange(value);
                  setSalaryMin(value[0].toString());
                  setSalaryMax(value[1].toString());
                }}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="location">Preferred Location</label>
                <input
                  type="text"
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g., New York, London, Remote"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experience">Experience Level</label>
                <select
                  id="experience"
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  className="form-select"
                >
                  <option value="">Select experience level</option>
                  {experienceLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={remote}
                  onChange={(e) => setRemote(e.target.checked)}
                  className="checkbox-input"
                />
                <span>Include remote opportunities</span>
              </label>
            </div>

            <button className="search-button" onClick={handleSearch}>
              <span className="material-icons">search</span>
              Search Jobs
            </button>
          </div>
        </div>

        <div className="comparison-section">
          <div className="comparison-header">
            <h2>Compare Your Current Position</h2>
            <button 
              className="toggle-comparison"
              onClick={() => setShowComparison(!showComparison)}
            >
              {showComparison ? 'Hide' : 'Show'} Comparison Tool
            </button>
          </div>

          {showComparison && (
            <div className="comparison-form">
              <p className="comparison-description">
                See how your current salary compares to market rates across different countries and cities
              </p>

              <div className="comparison-inputs">
                <div className="form-group">
                  <label htmlFor="current-position">Current Position</label>
                  <input
                    type="text"
                    id="current-position"
                    value={currentPosition}
                    onChange={(e) => setCurrentPosition(e.target.value)}
                    placeholder="Your current job title"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="current-salary">Current Salary</label>
                  <div className={`salary-input-group ${!currentSalaryInput.isValid ? 'error' : ''}`}>
                    <span className="currency-symbol">
                      <span className="material-icons">attach_money</span>
                    </span>
                    <input
                      type="number"
                      id="current-salary"
                      value={currentSalaryInput.value}
                      onChange={currentSalaryInput.onChange}
                      onBlur={currentSalaryInput.onBlur}
                      placeholder="Annual salary"
                      className="salary-input"
                    />
                  </div>
                  {currentSalaryInput.error && (
                    <span className="input-error">{currentSalaryInput.error}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="current-location">Current Location</label>
                  <input
                    type="text"
                    id="current-location"
                    value={currentLocation}
                    onChange={(e) => setCurrentLocation(e.target.value)}
                    placeholder="City or Country"
                    className="form-input"
                  />
                </div>

                <button className="compare-button" onClick={() => {
                  // Check if all fields are filled
                  const salaryValue = parseFloat(currentSalaryInput.value);
                  if (currentPosition && currentSalaryInput.value && !isNaN(salaryValue) && currentLocation) {
                    // Show modal with options for CV upload or default search
                    setShowCompareModal(true);
                  } else {
                    // If fields are not filled, show alert
                    alert('Please fill in all comparison fields first');
                  }
                }}>
                  <span className="material-icons">analytics</span>
                  Compare My Salary
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="features-section">
          <h2>Discover What Jobometry Offers</h2>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <span className="material-icons">public</span>
              </div>
              <h3>Global Salary Data</h3>
              <p>Compare salaries across countries with cost of living adjustments</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <span className="material-icons">business</span>
              </div>
              <h3>Company Insights</h3>
              <p>Glassdoor ratings, employee reviews, and company culture metrics</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <span className="material-icons">trending_up</span>
              </div>
              <h3>Market Trends</h3>
              <p>Real-time job market analytics and industry growth patterns</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <span className="material-icons">psychology</span>
              </div>
              <h3>Smart Matching</h3>
              <p>AI-powered job recommendations based on your skills and preferences</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <span className="material-icons">work</span>
              </div>
              <h3>Career Planning</h3>
              <p>Salary progression insights and career path recommendations</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <span className="material-icons">notifications</span>
              </div>
              <h3>Job Alerts</h3>
              <p>Get notified when new opportunities match your criteria</p>
            </div>
          </div>
        </div>

        <div className="stats-section">
          <div className="stats-container">
            <div className="stat-item">
              <div className="stat-number">50K+</div>
              <div className="stat-label">Active Jobs</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">10K+</div>
              <div className="stat-label">Companies</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">150+</div>
              <div className="stat-label">Countries</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">95%</div>
              <div className="stat-label">Match Accuracy</div>
            </div>
          </div>
        </div>

        <div className="cta-section">
          <h2>Ready to Find Your Dream Job?</h2>
          <p>Join thousands of professionals making smarter career decisions with data</p>
          <div className="cta-buttons">
            <button className="cta-primary" onClick={handleSearch}>
              Start Your Search
            </button>
            <button className="cta-secondary" onClick={() => setShowComingSoon(true)}>
              Learn More
            </button>
          </div>
        </div>
      </div>
      
      <ComingSoonModal 
        isOpen={showComingSoon}
        onClose={() => setShowComingSoon(false)}
        darkMode={darkMode}
      />
      
      {/* Compare Analysis Modal */}
      {showCompareModal && (
        <CompareAnalysisModal
          isOpen={showCompareModal}
          onClose={() => setShowCompareModal(false)}
          darkMode={darkMode}
          onLoginRequired={onLoginRequired}
          mode="compare-salary"
        />
      )}
    </div>
  );
};

export default LandingPage;